import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { plainToInstance } from 'class-transformer';
import { concat, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { appendHttpParams } from '../../../shared/functions/http-helper-functions';
import { HttpOperation } from '../../enums/http-operation';
import { FileModel } from '../../models/file/file.model';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../services/http-error.service';
import * as fromFilesActions from './files.actions';

@Injectable()
export class FilesEffects {
  fetchFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromFilesActions.fetchFileList),
      map((action) => action.payload),
      switchMap((payload) => {
        const endpoint = this.endpointService.getFilesSlug(HttpOperation.Get);

        const myParams = payload.params;
        const params = appendHttpParams(myParams);

        return this.http.get<unknown>(endpoint, { params }).pipe(
          map((json: unknown[]) => {
            const newFiles = plainToInstance(FileModel, json as unknown[]);
            return fromFilesActions.setFiles({ payload: newFiles });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Files',
              fromFilesActions.fetchFileList,
              fromFilesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  fetchSingleFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromFilesActions.fetchSingleFile),
      map((action) => action.payload),
      mergeMap((payload) => {
        const endpoint = this.endpointService.getFilesSlug(HttpOperation.Get);

        const myParams = payload.params;
        const params = appendHttpParams(myParams);

        return this.http.get(endpoint, { params, responseType: 'blob', observe: 'response' }).pipe(
          map((res: HttpResponse<Blob>) => {
            const blob = res.body!;
            const contentType = (res.headers.get('Content-Type') ?? blob.type ?? '').toLowerCase();
            const fileName =
              /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i.exec(
                res.headers.get('content-disposition'),
              )?.[1] ?? '';

            // If PDF, download it...
            if (payload.downloadToDisk) {
              return fromFilesActions.downloadPdf({ payload: { blob, fileName } }); // or a noop action
            }

            // ... otherwise create file model and return it.
            const file = new File([blob], fileName, { type: blob.type || contentType });

            const newFile = new FileModel();
            newFile.id = +myParams.file_id;
            newFile.file = file;

            return fromFilesActions.setSingleFile({ payload: newFile });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Files',
              fromFilesActions.fetchSingleFile,
              fromFilesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  uploadFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromFilesActions.uploadFile),
      map((action) => action.payload),
      mergeMap((payload) => {
        const endpoint = this.endpointService.getFilesSlug(HttpOperation.Post);

        const fileModel = payload.fileModel;
        const myParams = payload.params;

        const params = appendHttpParams(myParams);

        const form = new FormData();
        form.append('file', fileModel.file, fileModel.file.name);

        return this.http
          .put<unknown>(endpoint, form, {
            params,
            observe: 'events',
            reportProgress: true,
          })
          .pipe(
            map((event) => {
              switch (event.type) {
                case HttpEventType.Sent:
                  return fromFilesActions.uploadProgressSet({
                    payload: { uploadId: fileModel.id, progress: 0 },
                  });
                case HttpEventType.UploadProgress: {
                  const progress =
                    event.total && event.total > 0
                      ? Math.round((100 * (event.loaded ?? 0)) / event.total)
                      : Math.min(99, Math.round((event.loaded ?? 0) / 1024)); // fallback
                  return fromFilesActions.uploadProgressSet({
                    payload: { uploadId: fileModel.id, progress },
                  });
                }
                case HttpEventType.Response: {
                  const newFileModel = plainToInstance(FileModel, event.body);
                  return fromFilesActions.fileUploaded({
                    payload: newFileModel,
                    showNotification: false,
                  });
                }
                default:
                  return fromFilesActions.uploadProgressSet({
                    payload: { uploadId: fileModel.id, progress: -1 },
                  });
              }
            }),
            catchError((error) => {
              return this.httpErrorService.handleError(
                'Files',
                fromFilesActions.uploadFile,
                fromFilesActions,
                error,
              );
            }),
          );
      }),
    );
  });

  updateFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromFilesActions.updateFile),
      map((action) => action.payload),
      concatMap(({ toBeDeletedParams, fileModel, toBeUpdatedParams }) => {
        const hasOldFileToDelete = !!(toBeDeletedParams as any)?.file_id;

        // A) Old file but no new file -> delete the old file and return.
        if (!fileModel && hasOldFileToDelete) {
          return of(fromFilesActions.deleteFile({ payload: toBeDeletedParams }));
        }

        // B) No old file but new file -> upload immediately, then emit fileUpdated
        if (!hasOldFileToDelete) {
          return concat(
            of(
              fromFilesActions.uploadFile({
                payload: { fileModel, params: toBeUpdatedParams },
              }),
            ),
            this.actions$.pipe(
              ofType(fromFilesActions.fileUploaded),
              take(1),
              concatMap((updatedFile: any) =>
                of(
                  fromFilesActions.fileUpdated({
                    payload: updatedFile.payload,
                    showNotification: false,
                  }),
                ),
              ),
            ),
          );
        }

        // C) Old file and new file -> delete first, then upload, then emit fileUpdated
        return concat(
          of(fromFilesActions.deleteFile({ payload: toBeDeletedParams })),
          this.actions$.pipe(ofType(fromFilesActions.fileDeleted), take(1)),
          of(
            fromFilesActions.uploadFile({
              payload: { fileModel, params: toBeUpdatedParams },
            }),
          ),
          this.actions$.pipe(
            ofType(fromFilesActions.fileUploaded),
            take(1),
            concatMap((uploadedFile: any) =>
              of(
                fromFilesActions.fileUpdated({
                  payload: uploadedFile.payload,
                  showNotification: false,
                }),
              ),
            ),
          ),
        );
      }),
      catchError((error) =>
        this.httpErrorService.handleError(
          'Files',
          fromFilesActions.updateFile,
          fromFilesActions,
          error,
        ),
      ),
    ),
  );

  deleteFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromFilesActions.deleteFile),
      map((action) => action.payload),
      mergeMap((myParams) => {
        const endpoint = this.endpointService.getFilesSlug(HttpOperation.Delete);

        const params = appendHttpParams(myParams);

        return this.http.delete(endpoint, { params }).pipe(
          map(() => {
            const deletedFile = new FileModel();
            deletedFile.id = +myParams.file_id;
            return fromFilesActions.fileDeleted({ payload: deletedFile, showNotification: false });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Files',
              fromFilesActions.deleteFile,
              fromFilesActions,
              error,
            );
          }),
        );
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}
}
